export interface ExerciseNoteBlock {
  title: string;
  intro?: string;
  structure?: string;
  exampleFromText?: string;
  similarExample?: string;
  usage?: string;
}

const LABEL_PATTERNS: {
  key: keyof Pick<
    ExerciseNoteBlock,
    'structure' | 'exampleFromText' | 'similarExample' | 'usage'
  >;
  re: RegExp;
}[] = [
  { key: 'structure', re: /^(ساختار|structure)\s*[:：]\s*(.+)$/i },
  {
    key: 'exampleFromText',
    re: /^(از متن|از جمله|مثال از متن|example from (?:the )?text)\s*[:：]\s*(.+)$/i,
  },
  {
    key: 'similarExample',
    re: /^(جمله مشابه|مثال مشابه|similar example)\s*[:：]\s*(.+)$/i,
  },
  {
    key: 'usage',
    re: /^(?:🔹\s*)?(کاربرد|usage)\s*[:：]\s*(.+)$/i,
  },
];

/** Emoji keycap digits (1️⃣–9️⃣) → "1. " style for unified parsing */
const EMOJI_KEYCAP_DIGIT = /([1-9])\uFE0F?\u20E3\s*/g;

const NUMBERED_ITEM_START = /(?:^|\n)\d+\.\s/;

function normalizeExerciseNoteText(text: string): string {
  let normalized = text.trim().replace(/\r\n/g, '\n');
  normalized = normalized.replace(EMOJI_KEYCAP_DIGIT, '$1. ');

  const numberedStart = normalized.search(NUMBERED_ITEM_START);
  if (numberedStart > 0) {
    normalized = normalized.slice(numberedStart).trim();
  }

  return normalized;
}

function parseNoteSection(section: string): ExerciseNoteBlock {
  const lines = section
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const title = (lines[0] ?? '').replace(/^\d+\.\s*/, '').trim();
  const block: ExerciseNoteBlock = { title };
  const introLines: string[] = [];

  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i];
    let matched = false;
    for (const { key, re } of LABEL_PATTERNS) {
      const match = line.match(re);
      if (match) {
        block[key] = match[2].trim();
        matched = true;
        break;
      }
    }
    if (!matched) {
      introLines.push(line);
    }
  }

  if (introLines.length > 0) {
    block.intro = introLines.join('\n');
  }

  return block;
}

export function parseExerciseNotes(text: string | null | undefined): ExerciseNoteBlock[] {
  if (!text?.trim()) return [];

  const normalized = normalizeExerciseNoteText(text);
  if (!normalized) return [];

  const sections = normalized
    .split(/\n(?=\d+\.\s)/)
    .map((section) => section.trim())
    .filter(Boolean);

  return sections.map(parseNoteSection).filter((block) => block.title);
}
